import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { useAuth, API } from "@/App";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ChevronLeft, X, ChevronRight } from "lucide-react";
import { QrCode } from "@phosphor-icons/react";
import { BRAND } from "@/styles/brand";

// ═══════════════════════════════════════════════════════════
// BRAND COLORS - Unified via brand.js
// ═══════════════════════════════════════════════════════════
const C = BRAND.colors;
const COLORS = {
  background: C.bg,
  green: C.green,
  greenLight: C.greenLight,
  orange: C.orange,
  orangeLight: C.orangeLight,
  text: C.text,
  textLight: C.textMuted,
  white: C.textLight,
  cream: C.bgSecondary,
  yellow: C.tanLight,
  tan: C.tan,
};

// ═══════════════════════════════════════════════════════════
// VERSPIELTE ILLUSTRATIONEN
// ═══════════════════════════════════════════════════════════

// Süßes Geschenk - klein
const GiftIcon = ({ size = 40 }) => (
  <svg viewBox="0 0 100 100" style={{ width: size, height: size }}>
    <rect x="20" y="45" width="60" height="45" rx="6" fill={COLORS.orange}/>
    <rect x="15" y="32" width="70" height="18" rx="5" fill={COLORS.orange}/>
    <rect x="45" y="32" width="10" height="58" fill={COLORS.yellow} opacity="0.8"/>
    <ellipse cx="38" cy="28" rx="12" ry="8" fill={COLORS.yellow}/>
    <ellipse cx="62" cy="28" rx="12" ry="8" fill={COLORS.yellow}/>
    <circle cx="50" cy="32" r="6" fill={COLORS.yellow}/>
    <circle cx="38" cy="62" r="5" fill="#FFF"/>
    <circle cx="62" cy="62" r="5" fill="#FFF"/>
    <circle cx="36" cy="60" r="2" fill="#333"/>
    <circle cx="60" cy="60" r="2" fill="#333"/>
    <path d="M42 75 Q50 82 58 75" stroke="#FFF" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
  </svg>
);

// Kaffeetasse - klein
const CoffeeIcon = ({ size = 40 }) => (
  <svg viewBox="0 0 100 100" style={{ width: size, height: size }}>
    <path d="M25 35 L30 85 Q50 92 70 85 L75 35 Z" fill={COLORS.green}/>
    <ellipse cx="50" cy="35" rx="27" ry="8" fill={COLORS.greenLight}/>
    <circle cx="40" cy="60" r="4" fill="#FFF"/>
    <circle cx="60" cy="60" r="4" fill="#FFF"/>
    <circle cx="38" cy="58" r="1.5" fill="#333"/>
    <circle cx="58" cy="58" r="1.5" fill="#333"/>
    <path d="M42 72 Q50 80 58 72" stroke="#FFF" strokeWidth="2" fill="none" strokeLinecap="round"/>
    <path d="M75 42 Q88 48 88 60 Q88 72 75 68" stroke={COLORS.green} strokeWidth="6" fill="none"/>
  </svg>
);

// Tisch - klein
const TableIcon = ({ size = 40 }) => (
  <svg viewBox="0 0 100 100" style={{ width: size, height: size }}>
    <ellipse cx="50" cy="50" rx="35" ry="12" fill={COLORS.orange}/>
    <rect x="44" y="55" width="12" height="30" rx="3" fill={COLORS.orangeLight}/>
    <circle cx="40" cy="45" r="4" fill="#FFF"/>
    <circle cx="60" cy="45" r="4" fill="#FFF"/>
    <path d="M44 52 Q50 56 56 52" stroke="#FFF" strokeWidth="2" fill="none" strokeLinecap="round"/>
  </svg>
);

// Sparschwein - klein
const PiggyIcon = ({ size = 40 }) => (
  <svg viewBox="0 0 100 100" style={{ width: size, height: size }}>
    <ellipse cx="50" cy="55" rx="32" ry="26" fill={COLORS.green}/>
    <ellipse cx="30" cy="38" rx="7" ry="9" fill={COLORS.green}/>
    <ellipse cx="70" cy="38" rx="7" ry="9" fill={COLORS.green}/>
    <ellipse cx="50" cy="65" rx="10" ry="7" fill={COLORS.greenLight}/>
    <circle cx="46" cy="65" r="2" fill="#FFF"/>
    <circle cx="54" cy="65" r="2" fill="#FFF"/>
    <circle cx="38" cy="50" r="4" fill="#FFF"/>
    <circle cx="62" cy="50" r="4" fill="#FFF"/>
    <rect x="42" y="28" width="16" height="4" rx="2" fill={COLORS.yellow}/>
    <circle cx="50" cy="20" r="6" fill={COLORS.yellow}/>
  </svg>
);

// ═══════════════════════════════════════════════════════════
// STAMP CARD - VERSPIELT
// ═══════════════════════════════════════════════════════════
const StampCardCompact = ({ points = 0 }) => {
  const stampsNeeded = 10;
  const currentStamps = points % stampsNeeded;
  const freeDrinks = Math.floor(points / stampsNeeded);
  const stampsToGo = stampsNeeded - currentStamps;

  return (
    <motion.div 
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="mx-4 mb-4 p-4 rounded-3xl"
      style={{ backgroundColor: COLORS.white, boxShadow: "0 4px 20px rgba(0,0,0,0.06)" }}
    >
      <div className="flex items-center gap-4">
        <motion.div
          animate={{ rotate: [0, 10, -10, 0] }}
          transition={{ duration: 3, repeat: Infinity }}
        >
          <CoffeeIcon size={50} />
        </motion.div>
        <div className="flex-1">
          <p className="font-bold" style={{ color: COLORS.text }}>Stempelkarte</p>
          <p className="text-sm" style={{ color: COLORS.textLight }}>
            Noch <span style={{ color: COLORS.orange, fontWeight: "bold" }}>{stampsToGo}</span> bis zum Freigetränk!
          </p>
        </div>
        {freeDrinks > 0 && (
          <motion.div 
            className="px-3 py-1 rounded-full"
            style={{ backgroundColor: COLORS.orange }}
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            <span className="text-white text-sm font-bold">{freeDrinks} 🎁</span>
          </motion.div>
        )}
      </div>
      {/* Fortschrittsbalken */}
      <div className="flex gap-1.5 mt-3">
        {[...Array(10)].map((_, i) => (
          <motion.div 
            key={i}
            className="flex-1 h-2.5 rounded-full"
            style={{ backgroundColor: i < currentStamps ? COLORS.green : COLORS.cream }}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: i * 0.05 }}
          />
        ))}
      </div>
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════
// TAB NAVIGATION - VERSPIELT
// ═══════════════════════════════════════════════════════════

// Lieferservice Icon
const DeliveryIcon = ({ size = 24 }) => (
  <svg viewBox="0 0 100 100" style={{ width: size, height: size }}>
    <rect x="10" y="45" width="50" height="25" rx="4" fill={COLORS.textLight}/>
    <rect x="55" y="50" width="25" height="20" rx="3" fill={COLORS.textLight}/>
    <circle cx="25" cy="75" r="8" fill={COLORS.textLight}/>
    <circle cx="65" cy="75" r="8" fill={COLORS.textLight}/>
    <circle cx="25" cy="75" r="4" fill={COLORS.white}/>
    <circle cx="65" cy="75" r="4" fill={COLORS.white}/>
    <rect x="15" y="35" width="35" height="12" rx="3" fill={COLORS.textLight}/>
  </svg>
);

const TabNav = ({ activeTab, onTabChange, user, onAuthRequired }) => {
  const tabs = [
    { id: "coupons", label: "Coupons", icon: <GiftIcon size={24} />, requiresAuth: true },
    { id: "stamps", label: "Stempel", icon: <CoffeeIcon size={24} />, requiresAuth: true },
    { id: "reservation", label: "Tisch", icon: <TableIcon size={24} />, requiresAuth: true },
    { id: "invest", label: "Invest", icon: <PiggyIcon size={24} />, requiresAuth: false },
  ];
  
  return (
    <div className="mx-4 mb-4">
      <div className="flex gap-2">
        {tabs.map((tab) => (
          <motion.button
            key={tab.id}
            onClick={() => { if (tab.requiresAuth && !user) { onAuthRequired(); return; } onTabChange(tab.id); }}
            whileTap={{ scale: 0.92 }}
            className="flex-1 py-2 px-1 rounded-2xl flex flex-col items-center gap-1"
            style={{
              backgroundColor: activeTab === tab.id ? COLORS.green : COLORS.white,
              boxShadow: activeTab === tab.id ? `0 4px 15px ${COLORS.green}40` : "none",
            }}
          >
            <div style={{ filter: activeTab === tab.id ? "brightness(10)" : "none" }}>
              {tab.icon}
            </div>
            <span 
              className="text-[10px] font-bold"
              style={{ color: activeTab === tab.id ? COLORS.white : COLORS.text }}
            >
              {tab.label}
            </span>
          </motion.button>
        ))}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// COUNTDOWN TIMER
// ═══════════════════════════════════════════════════════════
const CountdownBadge = ({ daysLeft }) => {
  const isUrgent = daysLeft <= 3;
  return (
    <div 
      className="inline-flex items-center gap-1 px-3 py-1 rounded-full"
      style={{ backgroundColor: isUrgent ? COLORS.orange : COLORS.green }}
    >
      <span className="text-white text-xs font-bold">
        {isUrgent ? "🔥" : "⏰"} {daysLeft}d
      </span>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// ECHTZEIT COUNTDOWN
// ═══════════════════════════════════════════════════════════
const LiveCountdown = ({ daysLeft }) => {
  const [time, setTime] = useState(() => {
    const now = new Date();
    const endOfDay = new Date(now);
    endOfDay.setHours(23, 59, 59, 999);
    const msLeft = endOfDay - now;
    return {
      days: daysLeft - 1,
      hours: Math.floor((msLeft / (1000 * 60 * 60)) % 24),
      mins: Math.floor((msLeft / (1000 * 60)) % 60),
      secs: Math.floor((msLeft / 1000) % 60),
    };
  });
  
  useEffect(() => {
    const timer = setInterval(() => {
      setTime(prev => {
        let { days, hours, mins, secs } = prev;
        secs--;
        if (secs < 0) { secs = 59; mins--; }
        if (mins < 0) { mins = 59; hours--; }
        if (hours < 0) { hours = 23; days--; }
        if (days < 0) days = 0;
        return { days, hours, mins, secs };
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const isUrgent = time.days <= 2;
  
  return (
    <div 
      className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-bold"
      style={{ 
        backgroundColor: isUrgent ? COLORS.orange : "rgba(255,255,255,0.2)",
        color: "white"
      }}
    >
      {isUrgent && <span>🔥</span>}
      <span>{time.days}d</span>
      <span>{String(time.hours).padStart(2, '0')}:</span>
      <span>{String(time.mins).padStart(2, '0')}:</span>
      <span>{String(time.secs).padStart(2, '0')}</span>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// COUPON SUB-TABS (Aktiv / Eingelöst)
// ═══════════════════════════════════════════════════════════
const CouponSubTabs = ({ activeSubTab, onSubTabChange, redeemedCount }) => {
  return (
    <div className="flex gap-2 mb-4">
      <motion.button
        onClick={() => onSubTabChange("active")}
        whileTap={{ scale: 0.95 }}
        className="flex-1 py-2.5 px-4 rounded-xl font-bold text-sm flex items-center justify-center gap-2"
        style={{
          backgroundColor: activeSubTab === "active" ? COLORS.green : COLORS.white,
          color: activeSubTab === "active" ? COLORS.white : COLORS.text,
          boxShadow: activeSubTab === "active" ? `0 4px 12px ${COLORS.green}40` : "none",
        }}
      >
        <span>🎁</span>
        <span>Aktiv</span>
      </motion.button>
      <motion.button
        onClick={() => onSubTabChange("redeemed")}
        whileTap={{ scale: 0.95 }}
        className="flex-1 py-2.5 px-4 rounded-xl font-bold text-sm flex items-center justify-center gap-2"
        style={{
          backgroundColor: activeSubTab === "redeemed" ? COLORS.orange : COLORS.white,
          color: activeSubTab === "redeemed" ? COLORS.white : COLORS.text,
          boxShadow: activeSubTab === "redeemed" ? `0 4px 12px ${COLORS.orange}40` : "none",
        }}
      >
        <span>✅</span>
        <span>Eingelöst</span>
        {redeemedCount > 0 && (
          <span 
            className="px-2 py-0.5 rounded-full text-xs"
            style={{ 
              backgroundColor: activeSubTab === "redeemed" ? "rgba(255,255,255,0.25)" : COLORS.orange + "20",
              color: activeSubTab === "redeemed" ? COLORS.white : COLORS.orange,
            }}
          >
            {redeemedCount}
          </span>
        )}
      </motion.button>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// REDEEMED COUPON CARD
// ═══════════════════════════════════════════════════════════
const RedeemedCouponCard = ({ coupon, index }) => {
  // Coupon Bilder
  const couponImages = [
    "https://images.unsplash.com/photo-1509042239860-f550ce710b93?w=200&q=80",
    "https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=200&q=80",
    "https://images.unsplash.com/photo-1616690002178-4f1e025f412f?w=200&q=80",
  ];
  
  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "Vor kurzem";
    const date = new Date(dateStr);
    return date.toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric" });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.08 }}
      className="rounded-2xl overflow-hidden flex"
      style={{ 
        backgroundColor: COLORS.white,
        border: `2px solid ${COLORS.cream}`,
        opacity: 0.85,
      }}
    >
      {/* Linke Seite - Bild (Graustufen) */}
      <div className="w-20 relative overflow-hidden">
        <img 
          src={coupon.image_url || couponImages[index % couponImages.length]}
          alt={coupon.title}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: "grayscale(100%)" }}
        />
        {/* Eingelöst Badge */}
        <div 
          className="absolute inset-0 flex items-center justify-center"
          style={{ backgroundColor: "rgba(45, 90, 61, 0.75)" }}
        >
          <span className="text-2xl">✓</span>
        </div>
      </div>
      
      {/* Content */}
      <div className="flex-1 p-3">
        <div className="flex justify-between items-start">
          <div>
            <h3 className="font-bold text-sm" style={{ color: COLORS.text }}>
              {coupon.value || coupon.title}
            </h3>
            <p className="text-xs mt-0.5" style={{ color: COLORS.textLight }}>
              {coupon.description || coupon.title}
            </p>
          </div>
        </div>
        <div className="flex items-center justify-between mt-2 pt-2" style={{ borderTop: `1px solid ${COLORS.cream}` }}>
          <span 
            className="text-xs font-medium"
            style={{ color: COLORS.green }}
          >
            ✅ Eingelöst
          </span>
          <span className="text-xs" style={{ color: COLORS.textLight }}>
            {formatDate(coupon.redeemed_at)}
          </span>
        </div>
      </div>
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════
// EMPTY STATE FOR REDEEMED COUPONS
// ═══════════════════════════════════════════════════════════
const EmptyRedeemedState = () => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="text-center py-12"
  >
    <motion.div
      animate={{ y: [0, -8, 0] }}
      transition={{ duration: 2.5, repeat: Infinity }}
      className="mb-4"
    >
      <GiftIcon size={80} />
    </motion.div>
    <h3 className="text-lg font-bold mb-2" style={{ color: COLORS.text }}>
      Noch keine eingelösten Coupons
    </h3>
    <p className="text-sm px-8" style={{ color: COLORS.textLight }}>
      Löse deinen ersten Coupon ein und er wird hier angezeigt!
    </p>
  </motion.div>
);

// ═══════════════════════════════════════════════════════════
// COUPON CARD - MIT BILDERN & LIVE COUNTDOWN
// ═══════════════════════════════════════════════════════════
const CouponCard = ({ coupon, index, onClaim, isUserCoupon = false }) => {
  // Calculate days left from expires_at if available, otherwise use random
  const calculateDaysLeft = () => {
    if (coupon.expires_at) {
      const expiresAt = new Date(coupon.expires_at);
      const now = new Date();
      const diffTime = expiresAt - now;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return Math.max(1, diffDays);
    }
    return coupon.daysLeft || Math.floor(Math.random() * 20) + 1;
  };
  
  const daysLeft = calculateDaysLeft();
  
  // Verschiedene Coupon-Farben
  const cardColors = [
    { bg: COLORS.green, accent: COLORS.yellow },
    { bg: COLORS.orange, accent: COLORS.white },
    { bg: "#6B4423", accent: COLORS.yellow }, // Kaffeebraun
  ];
  const color = cardColors[index % cardColors.length];
  
  // Coupon Bilder
  const couponImages = [
    "/coupon-images/cappuccino.webp",
    "/coupon-images/matcha-latte.webp",
    "/coupon-images/cheesecake.webp",
    "/coupon-images/pasta-alfredo.webp",
    "/coupon-images/menu-deal.webp",
    "/coupon-images/croissant.webp",
  ];
  
  return (
    <motion.div
      initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.12, type: "spring" }}
      className="rounded-3xl overflow-hidden flex"
      style={{ 
        backgroundColor: color.bg,
        boxShadow: `0 8px 25px ${color.bg}40`,
        minHeight: "130px",
        cursor: "pointer",
      }}
      onClick={() => onClaim(coupon)}
      data-testid={`coupon-card-${coupon.id}`}
    >
      {/* Linke Seite - Bild */}
      <div className="w-28 relative overflow-hidden">
        <img 
          src={coupon.image_url || couponImages[index % couponImages.length]}
          alt={coupon.title}
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Overlay für bessere Lesbarkeit */}
        <div 
          className="absolute inset-0"
          style={{ background: `linear-gradient(90deg, transparent 0%, ${color.bg}90 100%)` }}
        />
        {/* Badge for already claimed coupons */}
        {isUserCoupon && (
          <div 
            className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-bold"
            style={{ backgroundColor: COLORS.yellow, color: COLORS.green }}
          >
            ✓ Aktiviert
          </div>
        )}
      </div>
      
      {/* Gestrichelte Linie */}
      <div className="flex flex-col items-center justify-center py-3 -ml-1">
        {[...Array(7)].map((_, i) => (
          <div 
            key={i} 
            className="w-0.5 h-2 mb-1 rounded-full"
            style={{ backgroundColor: "rgba(255,255,255,0.4)" }}
          />
        ))}
      </div>
      
      {/* Rechte Seite - Content */}
      <div className="flex-1 p-3 flex flex-col justify-between">
        <div>
          {/* Header mit Countdown */}
          <div className="flex justify-between items-start mb-1">
            <h3 className="text-lg font-black text-white leading-tight">
              {coupon.value || coupon.title}
            </h3>
            <LiveCountdown daysLeft={daysLeft} />
          </div>
          <p className="text-white text-xs opacity-80 line-clamp-2">
            {coupon.description || coupon.title}
          </p>
          {/* Show code for user coupons */}
          {isUserCoupon && coupon.code && (
            <p className="text-white text-[10px] opacity-60 mt-1 font-mono">
              {coupon.code}
            </p>
          )}
        </div>
        
        {/* Button */}
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => onClaim(coupon)}
          className="self-end px-4 py-1.5 rounded-full font-bold text-sm mt-2 flex items-center gap-1"
          style={{ 
            backgroundColor: color.accent, 
            color: color.bg,
          }}
          data-testid={`coupon-qr-btn-${coupon.id}`}
        >
          <span>{isUserCoupon ? "QR zeigen" : "QR-Code"}</span>
          <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
            <path d="M3 3h8v8H3V3zm2 2v4h4V5H5zm8-2h8v8h-8V3zm2 2v4h4V5h-4zM3 13h8v8H3v-8zm2 2v4h4v-4H5zm11-2h2v2h-2v-2zm-4 0h2v2h-2v-2zm2 2h2v2h-2v-2zm0 4h2v2h-2v-2zm2-2h2v2h-2v-2zm2-2h2v2h-2v-2zm0 4h2v2h-2v-2z"/>
          </svg>
        </motion.button>
      </div>
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════
// STAMPS TAB - VERSPIELT MIT QR-CODE
// ═══════════════════════════════════════════════════════════
const StampsTab = ({ points, memberCode, userName, userCard }) => {
  const [showQRModal, setShowQRModal] = useState(false);
  const stampsNeeded = 10;
  const currentStamps = points % stampsNeeded;
  const freeDrinks = Math.floor(points / stampsNeeded);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
      {/* Header */}
      <div className="text-center mb-4">
        <motion.div
          animate={{ y: [0, -8, 0], rotate: [0, 5, -5, 0] }}
          transition={{ duration: 3, repeat: Infinity }}
        >
          <CoffeeIcon size={80} />
        </motion.div>
        <h2 className="text-2xl font-black mt-2" style={{ color: COLORS.text }}>
          Stempelkarte
        </h2>
        <p className="text-sm" style={{ color: COLORS.textLight }}>10 Stempel = 1 Freigetränk!</p>
      </div>

      {/* QR-CODE BUTTON - PROMINENT */}
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={() => setShowQRModal(true)}
        className="w-full p-4 rounded-2xl flex items-center justify-center gap-3"
        style={{ 
          background: `linear-gradient(135deg, ${COLORS.green} 0%, #4A7C59 100%)`,
          boxShadow: `0 8px 25px ${COLORS.green}50`
        }}
        data-testid="show-stamp-qr-btn"
      >
        <motion.div
          animate={{ scale: [1, 1.1, 1] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        >
          <QrCode size={32} weight="fill" className="text-white" />
        </motion.div>
        <div className="text-left">
          <p className="text-white font-black text-lg">QR-Code zeigen</p>
          <p className="text-white/70 text-xs">Zum Stempel sammeln an der Kasse vorzeigen</p>
        </div>
        <ChevronRight size={24} className="text-white/70" />
      </motion.button>

      {/* Free drinks banner */}
      {freeDrinks > 0 && (
        <motion.div 
          className="p-4 rounded-2xl text-center"
          style={{ backgroundColor: COLORS.orange, boxShadow: `0 6px 20px ${COLORS.orange}40` }}
          animate={{ scale: [1, 1.02, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <span className="text-white text-lg font-bold">{freeDrinks} Freigetränk{freeDrinks > 1 ? 'e' : ''} verfügbar!</span>
        </motion.div>
      )}

      {/* Stamp grid - shows member card image as stamp */}
      <div className="p-5 rounded-3xl" style={{ backgroundColor: COLORS.white }}>
        <div className="grid grid-cols-5 gap-3">
          {[...Array(10)].map((_, i) => {
            const CARD_IMAGES = { pikachu: "/membercards/2.png", erdmaennchen: "/membercards/3.png", palestine: "/membercards/4.png", hund: "/membercards/5.png", katze: "/membercards/6.png" };
            const stampImg = CARD_IMAGES[userCard] || "/membercards/2.png";
            const isStamped = i < currentStamps;
            return (
              <motion.div
                key={i}
                className="aspect-square rounded-2xl flex items-center justify-center overflow-hidden relative"
                style={{ backgroundColor: isStamped ? COLORS.green + "15" : COLORS.cream, border: isStamped ? `2px solid ${COLORS.green}` : `2px dashed ${COLORS.cream}` }}
                initial={{ scale: 0, rotate: -10 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: i * 0.06, type: "spring" }}
              >
                {isStamped ? (
                  <img src={stampImg} alt="Stempel" className="w-full h-full object-cover rounded-xl" style={{ opacity: 0.85 }} />
                ) : i === 9 ? (
                  <span className="text-xl">🎁</span>
                ) : (
                  <span className="text-sm font-bold" style={{ color: COLORS.cream }}>{i + 1}</span>
                )}
              </motion.div>
            );
          })}
        </div>
        <p className="text-center mt-4 font-bold" style={{ color: COLORS.text }}>
          {currentStamps}/10 Stempel gesammelt
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3">
        <div className="p-4 rounded-2xl text-center" style={{ backgroundColor: COLORS.white }}>
          <p className="text-3xl font-black" style={{ color: COLORS.green }}>{points}</p>
          <p className="text-xs" style={{ color: COLORS.textLight }}>Gesamt</p>
        </div>
        <div className="p-4 rounded-2xl text-center" style={{ backgroundColor: COLORS.white }}>
          <p className="text-3xl font-black" style={{ color: COLORS.orange }}>{freeDrinks}</p>
          <p className="text-xs" style={{ color: COLORS.textLight }}>Freigetränke</p>
        </div>
      </div>

      {/* QR CODE MODAL */}
      <AnimatePresence>
        {showQRModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ backgroundColor: "rgba(0,0,0,0.8)", backdropFilter: "blur(8px)" }}
            onClick={() => setShowQRModal(false)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="w-full max-w-sm p-6 rounded-3xl text-center"
              style={{ backgroundColor: COLORS.white }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <motion.div
                animate={{ y: [0, -5, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="mb-4"
              >
                <CoffeeIcon size={60} />
              </motion.div>
              
              <h3 className="text-xl font-black mb-1" style={{ color: COLORS.text }}>
                Stempel sammeln
              </h3>
              <p className="text-sm mb-4" style={{ color: COLORS.textLight }}>
                Zeige diesen QR-Code an der Kasse
              </p>

              {/* QR Code */}
              <div 
                className="p-6 rounded-2xl mx-auto mb-4 inline-block"
                style={{ backgroundColor: COLORS.cream }}
              >
                {memberCode ? (
                  <QRCodeSVG 
                    value={memberCode}
                    size={180}
                    level="H"
                    style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                  />
                ) : (
                  <div className="w-44 h-44 flex items-center justify-center">
                    <p className="text-sm" style={{ color: COLORS.textMuted }}>
                      Bitte anmelden
                    </p>
                  </div>
                )}
              </div>

              {/* Member Code Display */}
              {memberCode && (
                <div 
                  className="p-3 rounded-xl mb-4"
                  style={{ backgroundColor: COLORS.cream }}
                >
                  <p className="text-xs" style={{ color: COLORS.textMuted }}>Dein Code</p>
                  <p className="text-lg font-mono font-black" style={{ color: COLORS.green }}>
                    {memberCode}
                  </p>
                  {userName && (
                    <p className="text-xs mt-1" style={{ color: COLORS.textMuted }}>{userName}</p>
                  )}
                </div>
              )}

              {/* Current Status */}
              <div 
                className="p-3 rounded-xl mb-4 flex items-center justify-center gap-4"
                style={{ backgroundColor: `${COLORS.green}15` }}
              >
                <div className="text-center">
                  <p className="text-2xl font-black" style={{ color: COLORS.green }}>{currentStamps}/10</p>
                  <p className="text-xs" style={{ color: COLORS.textMuted }}>Stempel</p>
                </div>
                {freeDrinks > 0 && (
                  <div className="text-center">
                    <p className="text-2xl font-black" style={{ color: COLORS.orange }}>{freeDrinks}</p>
                    <p className="text-xs" style={{ color: COLORS.textMuted }}>Freigetränke</p>
                  </div>
                )}
              </div>

              {/* Close Button */}
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowQRModal(false)}
                className="w-full py-3 rounded-xl font-bold text-white"
                style={{ backgroundColor: COLORS.green }}
              >
                Schließen
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════
// RESERVATION TAB - VERSPIELT
// ═══════════════════════════════════════════════════════════
const ReservationTab = () => {
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [guests, setGuests] = useState(2);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
      <div className="text-center mb-4">
        <motion.div
          animate={{ y: [0, -8, 0] }}
          transition={{ duration: 2.5, repeat: Infinity }}
        >
          <TableIcon size={80} />
        </motion.div>
        <h2 className="text-2xl font-black mt-2" style={{ color: COLORS.text }}>
          Tisch reservieren
        </h2>
      </div>

      <div className="space-y-3">
        <div className="p-4 rounded-2xl" style={{ backgroundColor: COLORS.white }}>
          <label className="block text-sm font-bold mb-2" style={{ color: COLORS.text }}>📅 Datum</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} 
            className="w-full p-3 rounded-xl border-2 focus:outline-none"
            style={{ borderColor: COLORS.cream, backgroundColor: COLORS.cream }} />
        </div>
        <div className="p-4 rounded-2xl" style={{ backgroundColor: COLORS.white }}>
          <label className="block text-sm font-bold mb-2" style={{ color: COLORS.text }}>⏰ Uhrzeit</label>
          <input type="time" value={time} onChange={(e) => setTime(e.target.value)}
            className="w-full p-3 rounded-xl border-2 focus:outline-none"
            style={{ borderColor: COLORS.cream, backgroundColor: COLORS.cream }} />
        </div>
        <div className="p-4 rounded-2xl" style={{ backgroundColor: COLORS.white }}>
          <label className="block text-sm font-bold mb-2" style={{ color: COLORS.text }}>Personen</label>
          <div className="flex items-center justify-center gap-6">
            <motion.button 
              whileTap={{ scale: 0.85 }} 
              onClick={() => setGuests(Math.max(1, guests - 1))}
              className="w-14 h-14 rounded-2xl text-2xl font-bold"
              style={{ backgroundColor: COLORS.cream, color: COLORS.text }}
            >-</motion.button>
            <span className="text-4xl font-black" style={{ color: COLORS.green }}>{guests}</span>
            <motion.button 
              whileTap={{ scale: 0.85 }} 
              onClick={() => setGuests(Math.min(10, guests + 1))}
              className="w-14 h-14 rounded-2xl text-2xl font-bold text-white"
              style={{ backgroundColor: COLORS.green }}
            >+</motion.button>
          </div>
        </div>
      </div>

      <motion.button
        whileTap={{ scale: 0.97 }}
        className="w-full py-4 rounded-2xl font-bold text-white text-lg"
        style={{ backgroundColor: COLORS.orange, boxShadow: `0 6px 20px ${COLORS.orange}40` }}
        onClick={async () => {
          if (!date || !time) { toast.error("Bitte Datum und Uhrzeit wählen"); return; }
          try {
            const token = localStorage.getItem("token");
            await axios.post(`${API}/reservations`, {
              date, time, guests: guests, name: "", phone: "", location: "aachen"
            }, { headers: { Authorization: `Bearer ${token}` } });
          } catch(e) {}
          toast.success("Tisch wurde reserviert! Wir freuen uns auf dich.", { duration: 4000 });
          setDate(""); setTime(""); setGuests(2);
        }}
      >
        Tisch reservieren
      </motion.button>
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════
// INVEST TAB - VERSPIELT
// ═══════════════════════════════════════════════════════════
const InvestTab = () => {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
      <div className="text-center py-8">
        <motion.div
          animate={{ y: [0, -8, 0], rotate: [0, 5, -5, 0] }}
          transition={{ duration: 3, repeat: Infinity }}
        >
          <PiggyIcon size={80} />
        </motion.div>
        <div className="mt-4 inline-block px-4 py-1.5 rounded-full" style={{ backgroundColor: COLORS.orange, color: "#fff" }}>
          <span className="text-sm font-bold">COMING SOON</span>
        </div>
        <h2 className="text-2xl font-black mt-4" style={{ color: COLORS.text, fontFamily: "'Space Grotesk', sans-serif" }}>
          Investiere in Franky's Farm
        </h2>
        <p className="text-base mt-3 px-4 leading-relaxed" style={{ color: COLORS.textLight, fontFamily: "'Figtree', sans-serif" }}>
          Bald kannst du mit investieren in neue Franky's Farm Stores in ganz Deutschland!
        </p>
        <div className="mt-6 p-5 rounded-2xl mx-4" style={{ backgroundColor: COLORS.white }}>
          <div className="grid grid-cols-3 gap-3">
            <div className="p-3 rounded-xl" style={{ backgroundColor: COLORS.cream }}>
              <p className="text-xl font-black" style={{ color: COLORS.green }}>5-10%</p>
              <p className="text-xs" style={{ color: COLORS.textLight }}>Rendite p.a.</p>
            </div>
            <div className="p-3 rounded-xl" style={{ backgroundColor: COLORS.cream }}>
              <p className="text-xl font-black" style={{ color: COLORS.orange }}>50\u20ac</p>
              <p className="text-xs" style={{ color: COLORS.textLight }}>ab</p>
            </div>
            <div className="p-3 rounded-xl" style={{ backgroundColor: COLORS.cream }}>
              <p className="text-xl font-black" style={{ color: COLORS.green }}>3+</p>
              <p className="text-xs" style={{ color: COLORS.textLight }}>Städte</p>
            </div>
          </div>
        </div>
        <p className="text-sm mt-4 px-6" style={{ color: COLORS.textLight }}>
          Wir informieren dich, sobald es losgeht. Bleib dran!
        </p>
      </div>
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════
// QR MODAL - ANTI-SCREENSHOT mit Live-Animationen
// ═══════════════════════════════════════════════════════════
const QRModal = ({ coupon, onClose }) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Live clock - updates every second to prove it's not a screenshot
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);
  
  if (!coupon) return null;
  
  // Make sure we have a real code
  const couponCode = coupon.code;
  if (!couponCode) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ backgroundColor: "rgba(0,0,0,0.8)" }}
        onClick={onClose}
      >
        <div className="bg-white p-6 rounded-2xl text-center">
          <p className="text-red-500 font-bold">Fehler: Kein Code vorhanden</p>
          <p className="text-sm text-gray-500 mt-2">Bitte Coupon erneut aktivieren</p>
          <button 
            onClick={onClose}
            className="mt-4 px-4 py-2 bg-green-600 text-white rounded-xl"
          >
            Schließen
          </button>
        </div>
      </motion.div>
    );
  }
  
  const timeString = currentTime.toLocaleTimeString('de-DE', { 
    hour: '2-digit', 
    minute: '2-digit', 
    second: '2-digit' 
  });
  
  return (
    <motion.div 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }} 
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4" 
      style={{ backgroundColor: "rgba(0,0,0,0.85)" }} 
      onClick={onClose}
    >
      <motion.div 
        initial={{ scale: 0.5, rotate: -10 }} 
        animate={{ scale: 1, rotate: 0 }} 
        transition={{ type: "spring", damping: 15 }}
        className="w-full max-w-sm rounded-3xl relative overflow-hidden"
        style={{ backgroundColor: COLORS.background }} 
        onClick={(e) => e.stopPropagation()}
      >
        {/* Animated gradient border - ANTI-SCREENSHOT */}
        <motion.div
          className="absolute inset-0 rounded-3xl"
          style={{
            background: `linear-gradient(90deg, ${COLORS.green}, ${COLORS.orange}, ${COLORS.green})`,
            backgroundSize: "200% 100%",
          }}
          animate={{
            backgroundPosition: ["0% 0%", "100% 0%", "0% 0%"],
          }}
          transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
        />
        
        {/* Inner content with padding for border effect */}
        <div 
          className="m-1 rounded-[22px] p-6 relative"
          style={{ backgroundColor: COLORS.background }}
        >
          {/* Floating sparkles - ANTI-SCREENSHOT */}
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 rounded-full"
              style={{ 
                backgroundColor: i % 2 === 0 ? COLORS.orange : COLORS.green,
                left: `${15 + i * 15}%`,
                top: i % 2 === 0 ? "8%" : "92%",
              }}
              animate={{
                y: [0, -10, 0],
                opacity: [0.3, 1, 0.3],
                scale: [0.8, 1.2, 0.8],
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                delay: i * 0.2,
              }}
            />
          ))}
          
          {/* Close button */}
          <motion.button 
            onClick={onClose} 
            whileTap={{ scale: 0.9 }}
            className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center"
            style={{ backgroundColor: COLORS.cream }}
          >
            <X className="w-4 h-4" style={{ color: COLORS.text }} />
          </motion.button>
          
          {/* Header with animated icon */}
          <div className="text-center mb-4">
            <motion.div
              animate={{ 
                rotate: [0, -10, 10, -10, 0],
                scale: [1, 1.1, 1, 1.1, 1],
              }}
              transition={{ duration: 2, repeat: Infinity }}
              className="inline-block"
            >
              <GiftIcon size={50} />
            </motion.div>
            <h3 
              className="text-xl font-black mt-2" 
              style={{ color: COLORS.green, fontFamily: "'Space Grotesk', sans-serif" }}
            >
              {coupon.value || coupon.title}
            </h3>
            <p className="text-sm" style={{ color: COLORS.textLight }}>
              {coupon.description || "Einmal einlösbar"}
            </p>
          </div>
          
          {/* QR Code with animated ring */}
          <div className="flex justify-center mb-4">
            <div className="relative">
              {/* Rotating ring - ANTI-SCREENSHOT */}
              <motion.div
                className="absolute -inset-3 rounded-2xl"
                style={{
                  border: `3px dashed ${COLORS.orange}`,
                }}
                animate={{ rotate: 360 }}
                transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
              />
              
              {/* Pulsing glow - ANTI-SCREENSHOT */}
              <motion.div
                className="absolute -inset-1 rounded-xl"
                style={{ backgroundColor: COLORS.green }}
                animate={{ 
                  opacity: [0.1, 0.3, 0.1],
                  scale: [1, 1.05, 1],
                }}
                transition={{ duration: 2, repeat: Infinity }}
              />
              
              {/* QR Code */}
              <div className="relative p-4 rounded-xl bg-white shadow-lg">
                <QRCodeSVG 
                  value={couponCode} 
                  size={140} 
                  bgColor="#FFFFFF" 
                  fgColor={COLORS.green} 
                  level="H"
                  imageSettings={{
                    src: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%232D5A3D'%3E%3Ccircle cx='12' cy='12' r='10'/%3E%3C/svg%3E",
                    height: 24,
                    width: 24,
                    excavate: true,
                  }}
                />
              </div>
            </div>
          </div>
          
          {/* Code display */}
          <div 
            className="text-center py-2 px-4 rounded-xl mb-4"
            style={{ backgroundColor: COLORS.cream }}
          >
            <p className="text-xs" style={{ color: COLORS.textLight }}>CODE</p>
            <p 
              className="text-lg font-mono font-bold tracking-wider"
              style={{ color: COLORS.green }}
            >
              {couponCode}
            </p>
          </div>
          
          {/* Live timestamp - ANTI-SCREENSHOT - proves it's live! */}
          <motion.div 
            className="flex items-center justify-center gap-2 py-2 rounded-xl"
            style={{ backgroundColor: COLORS.green }}
            animate={{ opacity: [0.9, 1, 0.9] }}
            transition={{ duration: 1, repeat: Infinity }}
          >
            <motion.div
              className="w-2 h-2 rounded-full bg-white"
              animate={{ scale: [1, 1.5, 1] }}
              transition={{ duration: 1, repeat: Infinity }}
            />
            <span className="text-white text-sm font-bold font-mono">
              LIVE · {timeString}
            </span>
            <motion.div
              className="w-2 h-2 rounded-full bg-white"
              animate={{ scale: [1, 1.5, 1] }}
              transition={{ duration: 1, repeat: Infinity, delay: 0.5 }}
            />
          </motion.div>
          
          {/* Moving wave at bottom - ANTI-SCREENSHOT */}
          <motion.div
            className="absolute bottom-0 left-0 right-0 h-1 rounded-b-3xl"
            style={{
              background: `linear-gradient(90deg, ${COLORS.orange}, ${COLORS.green}, ${COLORS.orange})`,
              backgroundSize: "200% 100%",
            }}
            animate={{
              backgroundPosition: ["0% 0%", "100% 0%", "0% 0%"],
            }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          />
        </div>
      </motion.div>
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════
// ONBOARDING MODAL - Für neue Kunden
// ═══════════════════════════════════════════════════════════
const OnboardingModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(0);
  const navigate = useNavigate();
  
  const steps = [
    {
      title: "Willkommen bei Franky's!",
      subtitle: "Lass uns dir kurz zeigen, was dich hier erwartet",
      icon: "👋",
      content: "Wir freuen uns, dass du da bist! Entdecke exklusive Vorteile, Rabatte und mehr.",
    },
    {
      title: "Coupons & Deals",
      subtitle: "Spare bei jedem Besuch",
      icon: "🎁",
      content: "Hole dir exklusive Coupons für Gratis-Getränke, Rabatte und mehr. Einfach QR-Code vorzeigen!",
      highlight: "coupons"
    },
    {
      title: "Stempelkarte",
      subtitle: "10 Stempel = Gratis Getränk",
      icon: "☕",
      content: "Mit jedem Kaffee sammelst du Stempel. Nach 10 Stempeln gibt's dein Getränk aufs Haus!",
      highlight: "stamps"
    },
    {
      title: "Tisch reservieren",
      subtitle: "Dein Platz wartet",
      icon: "🪑",
      content: "Reserviere deinen Lieblingstisch und genieße deinen Besuch ohne Wartezeit.",
      highlight: "reservation"
    },
    {
      title: "Membership",
      subtitle: "Täglich sparen",
      icon: "☕",
      content: "Mit dem Coffee Pass bekommst du jeden Tag einen Kaffee für nur 19,90€/Monat!",
      action: () => navigate("/membership"),
      actionText: "Membership ansehen"
    },
  ];
  
  if (!isOpen) return null;
  
  const currentStep = steps[step];
  const isLastStep = step === steps.length - 1;
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="w-full max-w-sm rounded-3xl overflow-hidden"
        style={{ backgroundColor: COLORS.white }}
        onClick={e => e.stopPropagation()}
      >
        {/* Progress Dots */}
        <div className="flex justify-center gap-2 pt-4">
          {steps.map((_, i) => (
            <div
              key={i}
              className="w-2 h-2 rounded-full transition-all"
              style={{ backgroundColor: i === step ? COLORS.green : COLORS.cream }}
            />
          ))}
        </div>
        
        {/* Content */}
        <div className="p-6 text-center">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-4"
          >
            <span className="text-5xl">{currentStep.icon}</span>
            <h2 className="text-xl font-black" style={{ color: COLORS.text }}>
              {currentStep.title}
            </h2>
            <p className="text-sm font-bold" style={{ color: COLORS.orange }}>
              {currentStep.subtitle}
            </p>
            <p className="text-sm" style={{ color: COLORS.textLight }}>
              {currentStep.content}
            </p>
          </motion.div>
        </div>
        
        {/* Actions */}
        <div className="p-4 flex gap-3">
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={onClose}
            className="flex-1 py-3 rounded-xl font-bold text-sm"
            style={{ backgroundColor: COLORS.cream, color: COLORS.textLight }}
          >
            Überspringen
          </motion.button>
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              if (currentStep.action) {
                currentStep.action();
                onClose();
              } else if (isLastStep) {
                onClose();
              } else {
                setStep(step + 1);
              }
            }}
            className="flex-1 py-3 rounded-xl font-bold text-sm text-white"
            style={{ backgroundColor: COLORS.green }}
          >
            {currentStep.actionText || (isLastStep ? "Los geht's!" : "Weiter")}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════
// MEMBERSHIP BANNER ILLUSTRATIONEN - Professionell & Spielerisch
// ═══════════════════════════════════════════════════════════
const MatchaIllustration = () => (
  <img src="/matcha-cup.webp" alt="Matcha" className="w-full h-full object-contain" style={{ filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.15))" }} />
);

const CoffeeIllustration = () => (
  <svg viewBox="0 0 50 50" className="w-full h-full">
    {/* Coffee cup body */}
    <path d="M10 18 L13 42 Q25 46 37 42 L40 18 Z" fill="#E8C49A"/>
    <ellipse cx="25" cy="18" rx="15" ry="5" fill="#D4A574"/>
    {/* Coffee surface */}
    <ellipse cx="25" cy="20" rx="12" ry="4" fill="#6B4423"/>
    {/* Latte art heart */}
    <path d="M22 19 Q22 17 24 17 Q25 17 25 19 Q25 17 26 17 Q28 17 28 19 Q28 22 25 24 Q22 22 22 19" fill="#E8C49A"/>
    {/* Handle */}
    <path d="M40 22 Q48 24 48 30 Q48 36 40 34" stroke="#D4A574" strokeWidth="4" fill="none" strokeLinecap="round"/>
    {/* Cute face */}
    <circle cx="20" cy="30" r="2.5" fill="#FFF"/>
    <circle cx="30" cy="30" r="2.5" fill="#FFF"/>
    <circle cx="19" cy="29" r="1.2" fill="#6B4423"/>
    <circle cx="29" cy="29" r="1.2" fill="#6B4423"/>
    <path d="M22 35 Q25 38 28 35" stroke="#6B4423" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
    {/* Rosy cheeks */}
    <ellipse cx="15" cy="32" rx="2" ry="1" fill="#FFB6C1" opacity="0.5"/>
    <ellipse cx="35" cy="32" rx="2" ry="1" fill="#FFB6C1" opacity="0.5"/>
    {/* Steam */}
    <path d="M20 12 Q22 8 20 4" stroke="#D4A574" strokeWidth="1.5" fill="none" strokeLinecap="round" opacity="0.5"/>
    <path d="M28 10 Q30 6 28 2" stroke="#D4A574" strokeWidth="1.5" fill="none" strokeLinecap="round" opacity="0.5"/>
    {/* Coffee beans floating */}
    <ellipse cx="6" cy="10" rx="3" ry="4" fill="#6B4423" transform="rotate(-20 6 10)"/>
    <path d="M6 7 Q5 10 6 13" stroke="#4A2F1A" strokeWidth="1" fill="none"/>
  </svg>
);

// MEMBERSHIP VORSCHLAG BANNER - KOMPAKT MIT SWIPE
// ═══════════════════════════════════════════════════════════
const MembershipBanner = ({ onDismiss }) => {
  const navigate = useNavigate();
  const [activeCard, setActiveCard] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  
  const plans = [
    {
      id: "matcha",
      name: "Matcha Club",
      Illustration: MatchaIllustration,
      label: "Beliebteste Wahl",
      badge: "BESTSELLER",
      price: "29,90€",
      benefit: "1x Matcha wöchentlich + 20% Rabatt",
      gradient: "linear-gradient(135deg, #7DB95A 0%, #4A7C59 100%)",
    },
    {
      id: "coffee",
      name: "Coffee Pass",
      Illustration: CoffeeIllustration,
      label: null,
      badge: null,
      price: "19,90€",
      benefit: "1x Kaffee-Getränk täglich",
      gradient: COLORS.green,
    }
  ];
  
  // Handle swipe
  const handleTouchStart = (e) => setTouchStart(e.touches[0].clientX);
  const handleTouchMove = (e) => setTouchEnd(e.touches[0].clientX);
  const handleTouchEnd = () => {
    if (touchStart - touchEnd > 50) setActiveCard(prev => (prev + 1) % plans.length);
    if (touchEnd - touchStart > 50) setActiveCard(prev => (prev - 1 + plans.length) % plans.length);
  };
  
  const currentPlan = plans[activeCard];
  const CurrentIllustration = currentPlan.Illustration;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="mx-4 mb-1 mt-1"
      style={{ zIndex: 1 }}
    >
      {/* Swipeable Card - KOMPAKT */}
      <div 
        className="relative overflow-hidden"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <AnimatePresence mode="wait">
          <motion.button
            key={currentPlan.id}
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -40 }}
            transition={{ duration: 0.2 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate("/membership")}
            className="w-full rounded-2xl px-3 text-left relative"
            style={{ background: currentPlan.gradient, height: "72px" }}
          >
            <div className="flex items-center gap-3 relative z-10 h-full">
              {/* Illustration - centered vertically */}
              <motion.div 
                className="w-32 h-32 flex-shrink-0 -ml-1"
                style={{ marginTop: "-30px", marginBottom: "-30px" }}
                animate={{ y: [0, -3, 0], rotate: [0, 2, -2, 0] }}
                transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
              >
                <CurrentIllustration />
              </motion.div>
              
              {/* Text */}
              <div className="flex-1 min-w-0">
                {currentPlan.label && (
                  <p className="text-[9px] text-white/60 font-semibold uppercase tracking-wider">
                    {currentPlan.label}
                  </p>
                )}
                <p className="text-sm font-black text-white truncate" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  {currentPlan.name}
                </p>
                <p className="text-[11px] text-white/75 truncate">{currentPlan.benefit}</p>
              </div>
              
              {/* Price */}
              <div className="text-right flex-shrink-0">
                <p className="text-lg font-black text-white">{currentPlan.price}</p>
                <p className="text-[9px] text-white/60">/Monat</p>
              </div>
            </div>
            
            {/* Badge */}
            {currentPlan.badge && (
              <div 
                className="absolute top-0 right-0 px-2 py-0.5 rounded-bl-lg text-[8px] font-bold"
                style={{ backgroundColor: "#FFD700", color: "#1A1A1A" }}
              >
                {currentPlan.badge}
              </div>
            )}
            
            {/* Close button */}
            <motion.div
              whileTap={{ scale: 0.9 }}
              onClick={(e) => { e.stopPropagation(); onDismiss(); }}
              className="absolute top-1.5 right-14 w-5 h-5 rounded-full flex items-center justify-center"
              style={{ backgroundColor: "rgba(255,255,255,0.2)" }}
            >
              <X className="w-3 h-3 text-white" />
            </motion.div>
          </motion.button>
        </AnimatePresence>
      </div>
      
      {/* Dots Indicator - Kompakt */}
      <div className="flex justify-center gap-1.5 mt-2">
        {plans.map((_, i) => (
          <button
            key={i}
            onClick={() => setActiveCard(i)}
            className="w-1.5 h-1.5 rounded-full transition-all duration-300"
            style={{ 
              backgroundColor: i === activeCard ? COLORS.green : COLORS.green + "30",
              transform: i === activeCard ? "scale(1.4)" : "scale(1)",
            }}
          />
        ))}
      </div>
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════
// DELIVERY COMING SOON MODAL
// ═══════════════════════════════════════════════════════════
const DeliveryComingSoonModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="w-full max-w-xs rounded-3xl p-6 text-center"
        style={{ backgroundColor: COLORS.white }}
        onClick={e => e.stopPropagation()}
      >
        <motion.div
          animate={{ y: [0, -10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="text-5xl mb-4"
        >
          🚴
        </motion.div>
        <h2 className="text-xl font-black mb-2" style={{ color: COLORS.text }}>
          Bald verfügbar!
        </h2>
        <p className="text-sm mb-4" style={{ color: COLORS.textLight }}>
          Bald kannst du dir dein Lieblingsgetränk und Snacks direkt nach Hause liefern lassen.
        </p>
        <p className="text-xs px-3 py-2 rounded-full inline-block mb-4" 
           style={{ backgroundColor: COLORS.orange + "20", color: COLORS.orange }}>
          Bald auch in Aachen
        </p>
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={onClose}
          className="w-full py-3 rounded-xl font-bold text-white"
          style={{ backgroundColor: COLORS.green }}
        >
          Verstanden
        </motion.button>
      </motion.div>
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════
export default function HomePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "coupons");

  // Sync tab with URL when navigating via BottomNav
  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab && tab !== activeTab) {
      setActiveTab(tab);
    }
  }, [searchParams]);
  const [couponSubTab, setCouponSubTab] = useState("active"); // "active" or "redeemed"
  const [templates, setTemplates] = useState([]);
  const [myCoupons, setMyCoupons] = useState([]); // User's claimed coupons
  const [redeemedCoupons, setRedeemedCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [showMembershipBanner, setShowMembershipBanner] = useState(true);
  const { user, token, setShowAuthModal, selectedLocation } = useAuth();
  const userPoints = typeof user?.stamps === 'number' ? user.stamps : 0;

  // Check if first visit
  useEffect(() => {

  }, []);

  useEffect(() => {
    fetchTemplates();
    if (user) {
      fetchMyCoupons();
      fetchRedeemedCoupons();
    }
  }, [selectedLocation, user]);

  // Refresh coupons when switching to coupons tab
  useEffect(() => {
    if (activeTab === "coupons" && user) {
      fetchMyCoupons();
      fetchRedeemedCoupons();
    }
  }, [activeTab, couponSubTab]);

  // Auto-refresh coupons to catch staff redemptions
  useEffect(() => {
    if (activeTab !== "coupons" || !user) return;
    const interval = setInterval(() => {
      fetchMyCoupons();
    }, 15000); // every 15 seconds
    return () => clearInterval(interval);
  }, [activeTab, user]);

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API}/coupons/templates${selectedLocation ? `?location=${selectedLocation}` : ""}`);
      setTemplates(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  // Fetch user's claimed (active) coupons
  const fetchMyCoupons = async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/coupons/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const all = res.data || [];
      // Split into active and redeemed
      const activeCoupons = all.filter(c => c.status === "active");
      const justRedeemed = all.filter(c => c.status === "redeemed");
      setMyCoupons(activeCoupons);
      // Merge redeemed into redeemedCoupons (avoid duplicates)
      if (justRedeemed.length > 0) {
        setRedeemedCoupons(prev => {
          const existingIds = prev.map(c => c.id);
          const newOnes = justRedeemed.filter(c => !existingIds.includes(c.id));
          return [...newOnes, ...prev];
        });
      }
    } catch (e) { 
      console.error("Error fetching my coupons:", e);
    }
  };

  const fetchRedeemedCoupons = async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/user/redeemed-coupons`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRedeemedCoupons(res.data || []);
    } catch (e) { 
      console.error("Error fetching redeemed coupons:", e);
      // Fallback to user object if API not available
      if (user?.redeemed_coupons) {
        setRedeemedCoupons(user.redeemed_coupons.map(id => ({
          id,
          title: "Eingelöster Coupon",
          value: "Coupon",
          redeemed_at: new Date().toISOString()
        })));
      }
    }
  };

  const handleClaim = async (couponTemplate) => {
    if (!user) { setShowAuthModal(true); return; }
    
    // Claim the coupon first to get the real code
    try {
      const res = await axios.post(
        `${API}/coupons/claim/${couponTemplate.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Set the claimed coupon with the real code
      setSelectedCoupon(res.data);
      // Add to my coupons list
      setMyCoupons(prev => [...prev, res.data]);
      toast.success("Coupon aktiviert!");
    } catch (err) {
      const msg = err.response?.data?.detail || "Fehler beim Aktivieren";
      toast.error(msg);
    }
  };

  // Show existing coupon QR code (for already claimed coupons)
  const handleShowCoupon = (coupon) => {
    // Refresh coupons to check if it was redeemed by staff
    fetchMyCoupons();
    setSelectedCoupon(coupon);
  };

  // Show ALL coupons: user's claimed ones + available templates
  const getActiveCoupons = () => {
    // Start with user's claimed active coupons
    const claimed = user ? myCoupons.filter(c => c.status === "active") : [];
    
    // Get ALL template IDs user has ever claimed (active + redeemed)
    const allMyTemplateIds = user ? [...myCoupons, ...redeemedCoupons].map(c => c.template_id) : [];
    
    // Only show templates user has NEVER claimed
    const unclaimed = templates.filter(t => !allMyTemplateIds.includes(t.id));
    
    // Combine: claimed active first, then unclaimed templates
    const allCoupons = [...claimed, ...unclaimed];
    
    if (allCoupons.length > 0) {
      return { coupons: allCoupons, isUserCoupons: false };
    }
    
    // Fallback demo coupons if nothing loaded
    return { coupons: [
      { id: "1", title: "Lade Coupons...", value: "...", description: "Einen Moment bitte", daysLeft: 1 },
    ], isUserCoupons: false };
  };

  const { coupons: displayCoupons, isUserCoupons } = getActiveCoupons();

  const tabTitles = {
    coupons: "Coupons & Deals",
    stamps: "Stempelkarte",
    reservation: "Reservierung",
    invest: "Investment"
  };

  return (
    <div className="min-h-screen pb-8" style={{ backgroundColor: COLORS.background }}>
      {/* Header */}
      <header className="px-4 pt-6 pb-4 flex items-center">
        <motion.button 
          whileTap={{ scale: 0.9 }} 
          onClick={() => navigate("/landing")}
          className="p-2 rounded-xl absolute left-4"
          style={{ backgroundColor: COLORS.white }}
        >
          <ChevronLeft className="w-5 h-5" style={{ color: COLORS.text }} />
        </motion.button>
        <h1 className="text-lg font-black w-full text-center" style={{ color: COLORS.text }}>
          {tabTitles[activeTab]}
        </h1>
      </header>

      {/* Membership Banner (manchmal anzeigen) */}
      {showMembershipBanner && !user?.membership?.active && (
        <MembershipBanner onDismiss={() => setShowMembershipBanner(false)} />
      )}


      {/* Tab Navigation */}
      {/* Tab navigation moved to BottomNav */}

      {/* Content */}
      <main className="px-4">
        <AnimatePresence mode="wait">
          {activeTab === "coupons" && (
            <motion.div key="coupons" className="space-y-4">
              {/* Sub-Tabs für Aktiv/Eingelöst */}
              <CouponSubTabs 
                activeSubTab={couponSubTab} 
                onSubTabChange={setCouponSubTab}
                redeemedCount={redeemedCoupons.length}
              />
              
              {couponSubTab === "active" ? (
                // Aktive Coupons
                loading ? (
                  [...Array(3)].map((_, i) => (
                    <div key={i} className="h-44 rounded-3xl animate-pulse" style={{ backgroundColor: COLORS.cream }} />
                  ))
                ) : displayCoupons.length > 0 ? (
                  displayCoupons.map((coupon, i) => (
                    <CouponCard 
                      key={coupon.id} 
                      coupon={coupon} 
                      index={i} 
                      onClaim={(coupon) => {
                        if (coupon.status === "redeemed") { 
                          toast.error("Dieser Coupon wurde bereits eingelöst"); 
                          fetchMyCoupons(); 
                          fetchRedeemedCoupons(); 
                          return; 
                        }
                        coupon.code ? handleShowCoupon(coupon) : handleClaim(coupon);
                      }}
                      isUserCoupon={!!coupon.code}
                    />
                  ))
                ) : (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center py-12"
                  >
                    <motion.div
                      animate={{ y: [0, -8, 0] }}
                      transition={{ duration: 2.5, repeat: Infinity }}
                      className="mb-4"
                    >
                      <GiftIcon size={80} />
                    </motion.div>
                    <h3 className="text-lg font-bold mb-2" style={{ color: COLORS.text }}>
                      Keine aktiven Coupons
                    </h3>
                    <p className="text-sm px-8" style={{ color: COLORS.textLight }}>
                      Entdecke neue Angebote und aktiviere deinen ersten Coupon!
                    </p>
                  </motion.div>
                )
              ) : (
                // Eingelöste Coupons
                redeemedCoupons.length > 0 ? (
                  <div className="space-y-3">
                    {redeemedCoupons.map((coupon, i) => (
                      <RedeemedCouponCard key={coupon.id || i} coupon={coupon} index={i} />
                    ))}
                  </div>
                ) : (
                  <EmptyRedeemedState />
                )
              )}
            </motion.div>
          )}
          {activeTab === "stamps" && user && <StampsTab key="stamps" points={userPoints} memberCode={user?.member_code} userName={user?.name} userCard={user?.member_card} />}
          {activeTab === "reservation" && user && <ReservationTab key="reservation" />}
          {activeTab === "invest" && <InvestTab key="invest" />}
        </AnimatePresence>
      </main>

      {/* Modals */}
      <AnimatePresence>
        {selectedCoupon && <QRModal coupon={selectedCoupon} onClose={() => setSelectedCoupon(null)} />}
        {showDeliveryModal && <DeliveryComingSoonModal isOpen={showDeliveryModal} onClose={() => setShowDeliveryModal(false)} />}
      </AnimatePresence>
    </div>
  );
}
